<template>
  <div class="poller">
    <div v-if="status === 'polling'" class="poller-row">
      <span class="spinner" />
      <span class="poller-text">{{ progress || "Processing…" }}</span>
    </div>
    <div v-else-if="status === 'done'" class="poller-row done">
      <span class="dot green" />
      <span class="poller-text">{{ doneMessage }}</span>
    </div>
    <div v-else-if="status === 'failed'" class="poller-row failed">
      <span class="dot red" />
      <span class="poller-text">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from "vue"

const props = defineProps({
  jobId:       { type: String, default: null },
  doneMessage: { type: String, default: "Complete" },
})

const emit = defineEmits(["done", "failed"])

const status       = ref("polling")
const progress     = ref(null)
const errorMessage = ref(null)

let interval = null

function stopPolling() {
  if (interval) {
    clearInterval(interval)
    interval = null
  }
}

async function poll() {
  if (!props.jobId) return
  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch(`/api/v1/jobs/${props.jobId}`, {
      headers: { "X-CSRF-Token": token },
    })
    if (!res.ok) return
    const data = await res.json()

    progress.value = data.progress

    if (data.status === "done") {
      status.value = "done"
      stopPolling()
      emit("done", data)
    } else if (data.status === "failed") {
      status.value   = "failed"
      errorMessage.value = data.error || "Job failed"
      stopPolling()
      emit("failed", data)
    }
  } catch {
    // network error — keep polling
  }
}

watch(
  () => props.jobId,
  (id) => {
    stopPolling()
    if (!id) return
    status.value   = "polling"
    progress.value = null
    interval = setInterval(poll, 2000)
    poll()
  },
  { immediate: true }
)

onUnmounted(stopPolling)
</script>

<style scoped>
.poller {
  margin-top: 10px;
}

.poller-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.6);
}

.poller-row.done   { color: #4ade80; }
.poller-row.failed { color: #f87171; }

.spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(245, 166, 35, 0.3);
  border-top-color: #f5a623;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot.green { background: #4ade80; box-shadow: 0 0 6px rgba(74, 222, 128, 0.5); }
.dot.red   { background: #f87171; box-shadow: 0 0 6px rgba(248, 113, 113, 0.5); }
</style>
